import { MutationTree } from 'vuex';
import { UserSetting, UserSettingState } from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// 全てのエラーをクリアする
const resetAllErrors = (state: UserSettingState): void => {
    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;
    showState.category = undefined;

    const beforeLoginState = state.beforeLogin;
    beforeLoginState.error = false;
    beforeLoginState.errorCode = undefined;
    beforeLoginState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;
    updateState.category = undefined;

    const connectionState = state.connection;
    connectionState.error = false;
    connectionState.errorTitle = undefined;
    connectionState.errorDetails = undefined;

    const importState = state.import;
    importState.error = false;
    importState.errorTitle = undefined;
    importState.errorDetails = undefined;

    const connectionIdpState = state.connectionIdp;
    connectionIdpState.error = false;
    connectionIdpState.errorTitle = undefined;
    connectionIdpState.errorDetails = undefined;

    const importIdpState = state.importIdp;
    importIdpState.error = false;
    importIdpState.errorTitle = undefined;
    importIdpState.errorDetails = undefined;
};

export const mutations: MutationTree<UserSettingState> = {
    // 設定取得系
    [types.SHOW_START](state, category) {
        resetAllErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
        showState.category = category;
    },

    [types.SHOW_SUCCESS](state, payload: { userSetting: UserSetting }) {
        state.userSetting = Object.assign({}, state.userSetting, payload.userSetting);

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
        showState.category = undefined;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 設定更新系
    [types.UPDATE_START](state, category) {
        resetAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
        updateState.category = category;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
        updateState.category = undefined;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ドメイン/多要素認証有効無効/多要素認証デバイス種別を取得
    [types.BEFORE_LOGIN_START](state) {
        resetAllErrors(state);
        const beforeLoginState = state.beforeLogin;
        beforeLoginState.isProcessing = true;
    },

    [types.BEFORE_LOGIN_SUCCESS](state, payload: { userSetting: UserSetting }) {
        state.userSetting = payload.userSetting;

        const beforeLoginState = state.beforeLogin;
        beforeLoginState.isProcessing = false;
    },

    [types.BEFORE_LOGIN_FAILURE](state, payload: { code: string; message: string }) {
        const beforeLoginState = state.beforeLogin;
        beforeLoginState.errorCode = payload.code;
        beforeLoginState.errorMsg = payload.message;
        beforeLoginState.error = true;
        beforeLoginState.isProcessing = false;
    },

    // 接続テスト系
    [types.CONNECTION_START](state) {
        resetAllErrors(state);
        const connectionState = state.connection;
        connectionState.isProcessing = true;
    },

    [types.CONNECTION_SUCCESS](state) {
        const connectionState = state.connection;
        connectionState.isProcessing = false;
    },

    [types.CONNECTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const connectionState = state.connection;
        connectionState.errorTitle = payload.errorTitle;
        connectionState.errorDetails = payload.errorDetails;
        connectionState.error = true;
        connectionState.isProcessing = false;
    },

    // ドメインユーザー取込系
    [types.IMPORT_START](state) {
        resetAllErrors(state);
        const importState = state.import;
        importState.isProcessing = true;
    },

    [types.IMPORT_SUCCESS](state) {
        const importState = state.import;
        importState.isProcessing = false;
    },

    [types.IMPORT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const importState = state.import;
        importState.errorTitle = payload.errorTitle;
        importState.errorDetails = payload.errorDetails;
        importState.error = true;
        importState.isProcessing = false;
    },

    // IdP接続テスト系
    [types.CONNECTION_IDP_START](state) {
        resetAllErrors(state);
        const connectionIdpState = state.connectionIdp;
        connectionIdpState.isProcessing = true;
    },

    [types.CONNECTION_IDP_SUCCESS](state) {
        const connectionIdpState = state.connectionIdp;
        connectionIdpState.isProcessing = false;
    },

    [types.CONNECTION_IDP_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const connectionIdpState = state.connectionIdp;
        connectionIdpState.errorTitle = payload.errorTitle;
        connectionIdpState.errorDetails = payload.errorDetails;
        connectionIdpState.error = true;
        connectionIdpState.isProcessing = false;
    },

    // IdPユーザー取込系
    [types.IMPORT_IDP_START](state) {
        resetAllErrors(state);
        const importIdpState = state.importIdp;
        importIdpState.isProcessing = true;
    },

    [types.IMPORT_IDP_SUCCESS](state) {
        const importIdpState = state.importIdp;
        importIdpState.isProcessing = false;
    },

    [types.IMPORT_IDP_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const importIdpState = state.importIdp;
        importIdpState.errorTitle = payload.errorTitle;
        importIdpState.errorDetails = payload.errorDetails;
        importIdpState.error = true;
        importIdpState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const beforeLoginState = state.beforeLogin;
        beforeLoginState.isProcessing = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const connectionState = state.connection;
        connectionState.isProcessing = false;

        const importState = state.import;
        importState.isProcessing = false;

        const connectionIdpState = state.connectionIdp;
        connectionIdpState.isProcessing = false;

        const importIdpState = state.importIdp;
        importIdpState.isProcessing = false;
    },
};
